import { ArrowLeft01Icon, ArrowRight01Icon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import React from 'react';
import {
  Box,
  Button,
  Container,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { Form, Formik } from 'formik';
import routes from '@/constants/routes';
import WebsiteDetails from './components/WebsiteDetails';
import WebsiteTemplates from './components/WebsiteTemplates';
import { useAppDispatch } from '@/hooks';
import { addOrganizationWebsite } from '@/features/organizations/slice';

const steps = [
  {
    title: 'Tema',
    component: WebsiteTemplates,
  },
  {
    title: 'Detalles',
    component: WebsiteDetails,
  },
];

const NewWebsiteWizard = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { organizationId } = useParams<{ organizationId: string }>();
  const [activeStep, setActiveStep] = React.useState(0);
  const [skipped, setSkipped] = React.useState(new Set<number>());

  const isStepSkipped = (step: number) => {
    return skipped.has(step);
  };

  const handleNext = () => {
    let newSkipped = skipped;
    if (isStepSkipped(activeStep)) {
      newSkipped = new Set(newSkipped.values());
      newSkipped.delete(activeStep);
    }

    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    setSkipped(newSkipped);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  return (
    <>
      <Stack padding={1} width={'200px'}>
        <Button
          variant="outlined"
          startIcon={<HugeiconsIcon icon={ArrowLeft01Icon} />}
          onClick={() =>
            navigate(
              routes.organizations.websites.main.replace(
                ':organizationId',
                organizationId ?? ''
              )
            )
          }
        >
          Regresar
        </Button>
      </Stack>
      <Container sx={{ width: '100%', marginTop: 4 }}>
        <Paper sx={{ padding: 4 }}>
          <Typography variant="h1" sx={{ marginBottom: 3 }}>
            Crear Website
          </Typography>

          <Stepper activeStep={activeStep}>
            {steps.map(({ title }, index) => {
              const stepProps: { completed?: boolean } = {};
              const labelProps: {
                optional?: React.ReactNode;
              } = {};

              if (isStepSkipped(index)) {
                stepProps.completed = false;
              }
              return (
                <Step key={title} {...stepProps}>
                  <StepLabel {...labelProps}>{title}</StepLabel>
                </Step>
              );
            })}
          </Stepper>

          <Formik
            initialValues={{
              title: '',
              template: '',
            }}
            onSubmit={async (values) => {
              if (!organizationId) return;

              const response = await dispatch(
                addOrganizationWebsite({
                  organizationId,
                  website: values,
                })
              );

              if (response.meta.requestStatus === 'fulfilled') {
                navigate(
                  `/comercios/${organizationId}/websites/${response?.payload?.website?.id}/editor`
                );
              }
            }}
          >
            {({ handleSubmit }) => (
              <Form>
                <>
                  <Box sx={{ paddingTop: 4, paddingBottom: 4 }}>
                    {React.createElement(steps[activeStep].component)}
                  </Box>
                  <Stack
                    direction="row"
                    spacing={2}
                    justifyContent="space-between"
                  >
                    <Button
                      color="inherit"
                      disabled={activeStep === 0}
                      onClick={handleBack}
                      sx={{ mr: 1 }}
                      size="large"
                      startIcon={<HugeiconsIcon icon={ArrowLeft01Icon} />}
                    >
                      Regresar
                    </Button>
                    <Button
                      endIcon={<HugeiconsIcon icon={ArrowRight01Icon} />}
                      onClick={
                        activeStep === steps.length - 1
                          ? handleSubmit
                          : handleNext
                      }
                    >
                      {activeStep === steps.length - 1
                        ? 'Finalizar'
                        : 'Continuar'}
                    </Button>
                  </Stack>
                </>
              </Form>
            )}
          </Formik>
        </Paper>
      </Container>
    </>
  );
};

export default NewWebsiteWizard;
